import React, { useState } from 'react';
import { Plus, Target, TrendingUp, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { mockSavings, formatCurrency } from '@/utils/mockData';
import { toast } from 'sonner';

export default function SavingsPage() {
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const handleCreateGoal = (e) => {
    e.preventDefault();
    toast.success('Savings goal created successfully!');
    setShowCreateDialog(false);
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="savings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-heading text-slate-900">My Savings</h1>
          <p className="text-slate-600 mt-1">Track and achieve your financial goals</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-goal-btn" className="bg-brand-blue text-white hover:bg-blue-700 rounded-full px-6 shadow-lg transition-all active:scale-95">
              <Plus className="w-5 h-5 mr-2" />
              Create Goal
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Savings Goal</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateGoal} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="goal-name">Goal Name</Label>
                <Input id="goal-name" placeholder="e.g., New Car" data-testid="goal-name-input" required className="h-12 rounded-lg" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="target-amount">Target Amount (₦)</Label>
                <Input id="target-amount" type="number" placeholder="0.00" data-testid="target-amount-input" required className="h-12 rounded-lg" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="frequency">Contribution Frequency</Label>
                <Select>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="frequency-select">
                    <SelectValue placeholder="How often?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contribution-amount">Contribution Amount (₦)</Label>
                <Input id="contribution-amount" type="number" placeholder="0.00" data-testid="contribution-amount-input" required className="h-12 rounded-lg" />
              </div>
              <Button type="submit" data-testid="submit-goal-btn" className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 font-semibold">
                Create Goal
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 text-brand-blue flex items-center justify-center">
              <Target className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Saved</p>
              <p className="text-2xl font-bold font-heading text-slate-900">₦2,080,000</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Active Goals</p>
              <p className="text-2xl font-bold font-heading text-slate-900">{mockSavings.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Next Contribution</p>
              <p className="text-2xl font-bold font-heading text-slate-900">₦270,000</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {mockSavings.map((saving) => {
          const progress = (saving.current / saving.target) * 100;
          return (
            <div key={saving.id} data-testid={`saving-card-${saving.id}`} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:border-brand-blue/50 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold font-heading text-slate-900">{saving.name}</h3>
                  <p className="text-sm text-slate-600 capitalize">{saving.frequency} contributions</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-brand-blue">{progress.toFixed(0)}%</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-blue rounded-full transition-all" style={{ width: `${progress}%` }}></div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <p className="text-slate-600">Current</p>
                    <p className="font-bold text-slate-900">{formatCurrency(saving.current)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-600">Target</p>
                    <p className="font-bold text-slate-900">{formatCurrency(saving.target)}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                  <div className="text-sm">
                    <p className="text-slate-600">Next Contribution</p>
                    <p className="font-semibold text-slate-900">{formatCurrency(saving.nextContribution)}</p>
                  </div>
                  <Button size="sm" data-testid={`contribute-btn-${saving.id}`} className="bg-brand-blue text-white hover:bg-blue-700 rounded-xl transition-all active:scale-95">
                    Add Money
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
